import React, { useState } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { getMapLegendTheme } from '../theme/themeStyles';
import InfoPopupDialog from '../components/InfoPopupDialog';
import { SATELLITE_LABELS, type IndexMapSatellite } from '../constants/indexMapSatellites';

interface AnalysisInfo {
  type: string;
  index: string;
  date: string;
  value?: string | null;
  spatialResolution?: string | null;
  spatialResolutionNote?: string | null;
  bandResolutions?: Record<string, number> | null;
  satellite?: string | null;
}

interface VisParams {
  min: number;
  max: number;
  palette: string[];
}

interface MapLegendProps {
  analysisInfo: AnalysisInfo | null;
  visParams: VisParams | null;
}

function resolutionPlainHint(resolution: string | null | undefined): string {
  if (resolution === '10 m') {
    return 'Quite detailed — good for seeing differences inside your farm.';
  }
  if (resolution === '20 m') {
    return 'Moderate detail — good for field zones, less for tiny patches.';
  }
  if (resolution === '30 m') {
    return 'Broader view — best for overall field patterns, not tiny spots.';
  }
  return 'How sharp this satellite picture is on the ground.';
}

export default function MapLegend({ analysisInfo, visParams }: MapLegendProps) {
  const { isDark } = useTheme();
  const lg = getMapLegendTheme(isDark);
  const [infoOpen, setInfoOpen] = useState(false);

  if (!analysisInfo) return null;

  const isBurnDetection = analysisInfo.type === 'Burn Detection';
  const isIndexMap = analysisInfo.type === 'Index Map';
  const hasResolution = Boolean(analysisInfo.spatialResolution);
  const satCode = (analysisInfo.satellite || '').toUpperCase() as IndexMapSatellite;
  const satLabel = SATELLITE_LABELS[satCode] || analysisInfo.satellite || null;

  return (
    <>
      {/* Left of the map, beside zoom; search stays top-right. */}
      <div
        className={`${lg.anchor} ${lg.shell}`}
      >
        {isBurnDetection ? (
          <div className="space-y-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className={lg.noBurnSwatch}></div>
                <span className={lg.burnNo}>No Burn</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-red-600 border-2 border-red-600"></div>
                <span className={lg.burnYes}>Burned</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            <div className={lg.infoCard}>
              <div className={`${lg.infoTitle} mb-1`}>{analysisInfo.index}</div>
              <div className={`${lg.body} truncate`}>{analysisInfo.date}</div>
              {hasResolution && (
                <div className={`${lg.body} mt-0.5 flex items-center gap-1`}>
                  <span>Detail: {analysisInfo.spatialResolution} per pixel</span>
                  {isIndexMap && (
                    <button
                      type="button"
                      title="What does this mean?"
                      aria-label="What does detail / spatial resolution mean?"
                      aria-expanded={infoOpen}
                      onClick={() => setInfoOpen(true)}
                      className={`shrink-0 p-0.5 rounded-full transition-colors ${lg.infoBtn}`}
                    >
                      <i className="ri-information-line text-xs sm:text-sm" />
                    </button>
                  )}
                </div>
              )}
              {analysisInfo.value && (
                <div className={`${lg.value} mt-1`}>{analysisInfo.value}</div>
              )}
            </div>

            {visParams && (
              <div className="space-y-2">
                <div className={lg.colorBarBorder}>
                  <div
                    className="h-full w-full"
                    style={{
                      background: `linear-gradient(to right, ${visParams.palette.join(', ')})`,
                    }}
                  />
                </div>
                <div className={lg.monoRange}>
                  <span>{visParams.min.toFixed(2)}</span>
                  <span>{visParams.max.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {isIndexMap && (
        <InfoPopupDialog
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          title="How sharp is this map?"
          accent="emerald"
        >
          <p>
            <span className="font-semibold">In simple words:</span> “Spatial resolution” means
            how big each tiny square (pixel) is on your farm. Think of the map as a grid of
            colored tiles laid over the field.
          </p>
          <p>
            <span className="font-semibold">Smaller number = sharper picture.</span> You can see
            smaller differences between nearby plants or zones.{" "}
            <span className="font-semibold">Larger number = blurrier picture.</span> Each tile
            covers more land, so fine details blend together.
          </p>
          <p>
            Everyday examples:
          </p>
          <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            <li>
              <span className="font-semibold">10 m</span> — each tile is about 10 × 10 meters
              (roughly a small room, or a few crop rows).
            </li>
            <li>
              <span className="font-semibold">20 m</span> — about 20 × 20 meters (a bit larger than
              a tennis court).
            </li>
            <li>
              <span className="font-semibold">30 m</span> — about 30 × 30 meters (close to a
              basketball court). Still useful to see how the whole field is doing.
            </li>
          </ul>
          {hasResolution && (
            <p>
              <span className="font-semibold">This map: </span>
              {analysisInfo.spatialResolution} per pixel.
              {' '}
              {resolutionPlainHint(analysisInfo.spatialResolution)}
              {satLabel ? ` Source: ${satLabel}.` : ''}
            </p>
          )}
        </InfoPopupDialog>
      )}
    </>
  );
}
